<script setup lang="ts">
import { CopyrightLabel } from '~/components';
import { AppTypography } from '~/components/base';
import { AppLogo } from '~/components/layout';
import { SocialMediaList } from '~/components/lists';
import FooterContent from '~/utils/contents/layout/Footer';
import {
  FOOTER_ABOUT,
  FOOTER_HELP,
  FOOTER_LINKS,
} from '~/utils/enums/navigation';

// The desktop and mobile Figma frames differ, so the two treatments are both
// rendered and toggled with CSS (not a JS breakpoint check, which would
// mismatch during SSR):
//
// - Desktop shows two columns — Links (without Home) and Help — with Terms of
//   Service / Privacy Policy moved down into the bottom bar and no copyright.
// - Mobile keeps three groups, with Terms/Privacy in their own About group,
//   and a centred copyright line above the social icons.
//
// `desktopOnly: false` marks the parts the desktop frame drops.
const columns = [
  { heading: 'Links', links: FOOTER_LINKS, desktopOnly: true },
  { heading: 'About', links: FOOTER_ABOUT, desktopOnly: false },
  { heading: 'Support', links: FOOTER_HELP, desktopOnly: true },
];

// Home is in the mobile Links group only — the desktop frame drops it.
// Campaigns shows at both sizes (the mobile frame omits it, but it's kept for
// parity with desktop so the section isn't unreachable from a phone footer).
const MOBILE_ONLY_LINKS = ['/'];
</script>

<template>
  <footer class="app-footer text-white">
    <div class="app-footer__border layout-size">
      <!-- Mobile stacks logo -> link groups in one column (Figma); from `lg`
           the logo/description sit left of the three link columns. -->
      <div
        class="app-footer__top flex flex-col justify-between gap-8 pb-6 lg:flex-row lg:pb-10"
      >
        <!-- No width clamp on desktop: the Figma left column is flex-1 so the
             description sits on a single line beside the link columns. It was
             previously capped at max-w-sm, which wrapped it into four lines. -->
        <div class="lg:min-w-0 lg:flex-1">
          <AppLogo
            class="mb-4"
            data-testid="logo"
            src="/logo-footer.svg"
            width="169px"
            height="48px"
          />
          <AppTypography
            type="subheadline"
            class="app-footer__description hidden lg:block"
          >
            {{ FooterContent.description }}
          </AppTypography>
        </div>
        <div class="flex flex-col gap-6 lg:flex-row lg:gap-20">
          <div
            v-for="col in columns"
            :key="col.heading"
            class="flex flex-col gap-4 lg:gap-6"
            :class="{ 'lg:hidden': !col.desktopOnly }"
          >
            <AppTypography type="subheadline" class="app-footer__heading">
              {{ col.heading }}
            </AppTypography>
            <NuxtLink
              v-for="link in col.links"
              :key="link.to"
              :to="link.to"
              class="no-underline"
              :class="{ 'lg:hidden': MOBILE_ONLY_LINKS.includes(link.to) }"
            >
              <AppTypography type="subheadline-bold" class="text-white">
                {{ link.label }}
              </AppTypography>
            </NuxtLink>
          </div>
        </div>
      </div>
      <div
        class="app-footer__bottom flex flex-col items-center gap-4 pt-4 text-center lg:flex-row lg:justify-between lg:gap-6 lg:pt-10 lg:text-left"
      >
        <!-- Desktop puts Terms/Privacy at the bottom left instead of in an
             About column; mobile keeps the centred copyright line. -->
        <div class="hidden lg:flex lg:items-center lg:gap-6">
          <NuxtLink
            v-for="link in FOOTER_ABOUT"
            :key="link.to"
            :to="link.to"
            class="app-footer__legal-link no-underline"
          >
            <AppTypography type="body">{{ link.label }}</AppTypography>
          </NuxtLink>
        </div>
        <CopyrightLabel
          data-testid="footer-copyright"
          type="footnote"
          class="app-footer__copyright lg:hidden"
        />
        <SocialMediaList data-testid="social-media-list" variant="negative" />
      </div>
    </div>
  </footer>
</template>

<style lang="scss" scoped>
.app-footer {
  // Muted sage for the group headings and the copyright line, and the warm
  // grey divider — Figma's text/brand---tertiary and borders/secondary, which
  // have no token in this theme yet.
  $footer-muted: #9faaa5;
  $footer-divider: #827b72;

  width: 100%;
  background-color: $surface-color-brand;
  // Figma mobile: 24px block / 16px inline padding; the wider desktop
  // breathing room is kept above `lg`.
  padding: $spacing-xl 0;

  @media (min-width: 1024px) {
    padding: $spacing-6xl 0;
  }

  &__top {
    border-bottom: 1px solid $footer-divider;
  }

  &__heading {
    color: $footer-muted;

    // Figma: desktop column headers ("Links"/"Support") are Body/Regular
    // 16px, not the 14px Subheadline size used for the mobile groups.
    @media (min-width: 1024px) {
      @include font-body;
    }
  }

  &__description {
    // Muted sage rather than white-at-70%-opacity, matching the Figma token.
    color: $footer-muted;
    // Keep the blurb on one line once there's room for it; below that it wraps
    // rather than overflowing into the link columns.
    @media (min-width: 1400px) {
      white-space: nowrap;
    }
  }

  &__copyright {
    color: $footer-muted;
  }

  // Bottom-bar legal links are muted, not white like the column links
  // (Figma desktop).
  &__legal-link {
    color: $footer-muted;
  }

  // The global `.router-link-active` rule paints a near-white background on
  // the current-route link; on this dark footer that renders as a white box.
  // Neutralize it for footer links.
  :deep(.router-link-active) {
    background-color: transparent;
  }
}
</style>
